import * as Collapsible from '@radix-ui/react-collapsible'
import { IconChevronDown } from '@tabler/icons-react'
import { motion } from 'motion/react'
import { ReactNode, Ref, useState } from 'react'

import { AstraIcon } from '../AstraIcon/AstraIcon'
import { astraTwMerge } from '../Utilities/astraTwMerge'

type Props = Collapsible.CollapsibleProps & {
    header: ReactNode
    children?: ReactNode
    onAnimationStart?: () => void
    onAnimationEnd?: () => void
    color?: 'magic' | 'default'
    ref?: Ref<HTMLDivElement>
    noPadding?: boolean
    noDivider?: boolean
}

const styles: Record<Exclude<Props['color'], undefined>, string> = {
    default: 'bg-surface-base',
    magic: 'bg-magic-subtle-rest border-magic-subtle',
}

export const AstraCollapsibleCard = ({
    ref,
    header,
    children,
    onAnimationStart,
    onAnimationEnd,
    open,
    onOpenChange,
    defaultOpen = true,
    color = 'default',
    className,
    noPadding = false,
    noDivider = false,
    ...props
}: Props) => {
    const [internalOpen, setInternalOpen] = useState(defaultOpen)

    const isOpen = open ?? internalOpen

    function toggleOpen(newValue: boolean) {
        onOpenChange ? onOpenChange(newValue) : setInternalOpen(newValue)
    }

    return (
        <Collapsible.Root
            className={astraTwMerge(`flex flex-col rounded-md border border-base ${styles[color]}`, className)}
            open={isOpen}
            onOpenChange={toggleOpen}
            {...props}
        >
            <Collapsible.Trigger className="flex w-full cursor-pointer items-center justify-between gap-x-2 px-4 py-2 text-left">
                <div className="flex-1">{header}</div>
                <div className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                    <AstraIcon icon={IconChevronDown} />
                </div>
            </Collapsible.Trigger>

            <motion.div
                ref={ref}
                initial={false}
                className="overflow-hidden"
                transition={{ duration: 0.2 }}
                animate={{ height: isOpen ? 'auto' : 0 }}
                onAnimationStart={onAnimationStart}
                onAnimationComplete={onAnimationEnd}
            >
                {!noDivider && <hr />}
                <Collapsible.Content forceMount className={noPadding ? '' : 'px-4 py-2'}>
                    {children}
                </Collapsible.Content>
            </motion.div>
        </Collapsible.Root>
    )
}
